// Host-aware portal split (MVP 4.0).
//
// The SAME Vite SPA bundle serves two front-facing portals from one backend and
// one Fly deployment, distinguished only by hostname:
//   - apex `interview-tool.click`     → recruiter / testing platform (/admin/*)
//   - `ops.interview-tool.click`      → operations portal (the /backoffice tree)
//
// Operators authenticate with their normal recruiter credentials; the shared
// parent-domain session cookie (`.interview-tool.click`) flows to both hosts, so
// there is no second login system — `platform_role` gates the back office as
// before. The host only decides which routes the SPA mounts (see App.tsx).
//
// Evaluated once at module load: the hostname can't change without a full page
// reload, so a const is correct and avoids re-deriving it on every render. The
// `typeof window` guard keeps it safe under jsdom/test imports.
export const isOpsPortal =
  typeof window !== "undefined" &&
  window.location.hostname.startsWith("ops.");

// The recruiter app's hostname, as seen from wherever we currently are. Derived
// by stripping the `ops.` label rather than hardcoding the production domain, so
// it stays correct on staging, on a preview host, and on localhost.
//
// Needed because the two portals are separate ORIGINS: linking from ops.* into
// the recruiter app is a real page load, not a router navigation. Use
// APEX_ORIGIN below for any such link — this bare host is only its building
// block, and pairing it with a literal scheme is the bug that block documents,
// which is why it is no longer exported (SIMP-12).
const APEX_HOST =
  typeof window === "undefined"
    ? ""
    : window.location.host.replace(/^ops\./, "");

// The recruiter app's full ORIGIN — what a cross-host link actually needs.
//
// Exists because APEX_HOST alone invites the mistake it was written to avoid:
// hardcoding `https://` next to a derived host. That is what shipped, and it
// dead-ended the one link on the page — `http://ops.localhost:5173` (the local
// ops host used in TEST.md's Playwright walkthroughs) produced
// `https://localhost:5173/...`, a TLS handshake against a plain-HTTP Vite dev
// server. Production was fine, so the escape hatch was unverifiable exactly
// where you would verify it. Carrying the current protocol over is correct on
// both: the two portals are always served the same way.
export const APEX_ORIGIN =
  typeof window === "undefined"
    ? ""
    : `${window.location.protocol}//${APEX_HOST}`;
